import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import pLimit from 'p-limit';
import type { ApiEndpoints } from '@avisoma/shared';
import backend from '../../services/backend';
import { useToast } from '../../components/Toast';
import { useAuth } from '../../hooks/useAuth';
import {
  evictStaleQueueMembers,
  groupSubmissionBatchId,
  participatingCount,
  planQueueRefresh,
  planSubmissions,
  queueViewIsLoading,
  resolveVerdictOutcomes,
  targetCategory,
  type SubmissionOutcome,
  type VerdictSubmission,
} from './groupVerdict';

type QueueResponse = ApiEndpoints<string>['/api/curation/queue']['GET']['response'];
export type CurationItem = QueueResponse['items'][number];

type SimilarResponse = ApiEndpoints<string>['/api/curation/similar-pages/:pageId']['GET']['response'];
export type CurationSimilarItem = SimilarResponse['items'][number];

export type SessionStats = {
  reviewed: number;
  corrected: number;
  streak: number;
  touchedCategories: Set<string>;
};

const EMPTY_STATS: SessionStats = { reviewed: 0, corrected: 0, streak: 0, touchedCategories: new Set() };

// A grouped verdict fans out one POST per page. Cap the in-flight ones: a full group is
// 21 pages, and 21 simultaneous writes buy nothing while each one takes a page-level lock.
const SUBMIT_CONCURRENCY = 5;

async function submitVerdict(submission: VerdictSubmission, batchId?: string): Promise<void> {
  await backend.postCurationReview({
    pageId: submission.pageId,
    verdict: submission.verdict,
    expectedCategory: submission.expectedCategory,
    newCategory: submission.verdict === 'corrected' ? submission.newCategory : undefined,
    source: submission.source,
    ...(batchId ? { batchId } : {}),
  });
}

export function useCurationSession(filters: { source?: string; category?: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { user } = useAuth();
  const [handled, setHandled] = useState<Set<string>>(new Set());
  const [awaitingQueueRefresh, setAwaitingQueueRefresh] = useState(false);
  const [stats, setStats] = useState<SessionStats>(EMPTY_STATS);

  const filterKey = `${filters.source ?? ''}|${filters.category ?? ''}`;
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setHandled(new Set());
  }

  // The queue response is user-scoped — the second_opinion set depends on the
  // requester's id, and priorReviews names depend on their role — so the cache must
  // not be shared across users/emulated roles. Mirrors the leaderboard's key.
  const queue = useQuery({
    queryKey: ['curation', 'queue', user?.id, user?.role, filters],
    queryFn: async () => (await backend.getCurationQueue({ ...filters, limit: 50 })).data,
  });

  const pending = useMemo(
    () => (queue.data?.items ?? []).filter((item) => !handled.has(item.pageId)),
    [queue.data, handled],
  );
  const current: CurationItem | undefined = pending[0];

  // Grouping: the still-flagged pages that look like the page on screen (DR-3318).
  // Keyed on the anchor alone — the cohort is anchor-relative and role-independent.
  const similar = useQuery({
    queryKey: ['curation', 'similar', current?.pageId],
    queryFn: async () => (await backend.getCurationSimilarPages(current!.pageId)).data,
    enabled: !!current,
  });

  // Deselection (rather than selection) is tracked so a member arriving from a later
  // fetch is selected by default, and it resets whenever the anchor changes.
  const [anchorId, setAnchorId] = useState<string | undefined>(current?.pageId);
  const [deselected, setDeselected] = useState<Set<string>>(new Set());
  if (anchorId !== current?.pageId) {
    setAnchorId(current?.pageId);
    setDeselected(new Set());
  }

  // An errored cohort is a SOLO review, not a stale one: a failing refetch leaves the
  // previous fetch's data in the cache, and grouping onto members whose categories may
  // have moved is worse than not grouping at all. Dropping them also settles the
  // isError-while-refetching case below — the controls unlock onto a group of one.
  const members = useMemo(
    () =>
      similar.isError ? [] : (similar.data?.items ?? []).filter((member) => !handled.has(member.pageId)),
    [similar.data, similar.isError, handled],
  );
  const selectedMembers = useMemo(
    () => members.filter((member) => !deselected.has(member.pageId)),
    [members, deselected],
  );

  const selectedMemberIds = useMemo(
    () => new Set(selectedMembers.map((member) => member.pageId)),
    [selectedMembers],
  );

  // The cohort for THIS anchor has come back and is not being refetched — until then the
  // members are unknown or stale, so a verdict cast now would land on an arbitrary subset
  // of the group, or on members whose categories have already moved. `isFetched` alone is
  // not enough: it stays true over a refetch of cached data. Failure counts as settled —
  // a cohort we cannot fetch is a solo review, not a lock-out.
  const membersSettled = similar.isError || (similar.isFetched && !similar.isFetching);

  const toggleMember = useCallback((pageId: string) => {
    setDeselected((prev) => {
      const next = new Set(prev);
      if (next.has(pageId)) next.delete(pageId);
      else next.add(pageId);
      return next;
    });
  }, []);

  const verdictMutation = useMutation({
    mutationFn: async (input: {
      item: CurationItem;
      verdict: 'confirmed' | 'corrected';
      newCategory?: string | null;
      // Exactly the members the curator could see selected when they clicked — nothing hidden.
      members: CurationSimilarItem[];
    }): Promise<SubmissionOutcome[]> => {
      const submissions = planSubmissions({
        anchor: input.item,
        verdict: input.verdict,
        newCategory: input.newCategory,
        members: input.members,
      });

      // allSettled, not all: one page's 409 must not discard the verdicts that landed.
      const limit = pLimit(SUBMIT_CONCURRENCY);
      const batchId = groupSubmissionBatchId(submissions.length);
      const settled = await Promise.allSettled(
        submissions.map((submission) => limit(() => submitVerdict(submission, batchId))),
      );

      return submissions.map((submission, index) => {
        const result = settled[index];
        if (result.status === 'fulfilled') return { submission, ok: true };
        const error = result.reason as { response?: { status?: number } };
        return { submission, ok: false, status: error?.response?.status };
      });
    },
    onSuccess: (outcomes, input) => {
      const resolution = resolveVerdictOutcomes(
        outcomes,
        targetCategory(input.item.currentCategory, input.verdict, input.newCategory),
      );

      if (resolution.leaving.length > 0) {
        setHandled((prev) => new Set([...prev, ...resolution.leaving]));
      }

      if (resolution.reviewed > 0) {
        setStats((prev) => ({
          reviewed: prev.reviewed + resolution.reviewed,
          corrected: prev.corrected + resolution.corrected,
          streak: prev.streak + resolution.reviewed,
          touchedCategories:
            resolution.touchedCategories.length > 0
              ? new Set([...prev.touchedCategories, ...resolution.touchedCategories])
              : prev.touchedCategories,
        }));
      }

      if (resolution.toast.kind !== 'none') {
        toast[resolution.toast.kind](resolution.toast.message);
      }

      const queuePlan = planQueueRefresh(resolution);
      if (queuePlan.evictIds.length > 0) {
        queryClient.setQueriesData<QueueResponse>({ queryKey: ['curation', 'queue'] }, (cached) =>
          cached
            ? { ...cached, items: evictStaleQueueMembers(cached.items, queuePlan.evictIds) }
            : cached,
        );
      }

      if (queuePlan.refetch) {
        // Hold the empty-queue panel until this refetch settles — a background
        // refetch later must not reuse this latch (window focus / remount).
        setAwaitingQueueRefresh(true);
        void queryClient
          .refetchQueries({ queryKey: ['curation', 'queue'] })
          .finally(() => setAwaitingQueueRefresh(false));
      } else if (resolution.reviewed > 0 || queuePlan.evictIds.length > 0) {
        // Mark all queue variations stale so revisiting a filter refetches fresh
        // (server suppression then excludes this just-reviewed page). refetchType
        // 'none' avoids disrupting the current view — `handled` already hides it.
        queryClient.invalidateQueries({
          queryKey: ['curation', 'queue'],
          refetchType: 'none',
        });
      }

      if (resolution.reviewed > 0 || resolution.staleMemberIds.length > 0) {
        // The grouping cohort is "pages with an OPEN flag": a landed verdict resolves the
        // flag, so every cached cohort that listed those pages is stale too.
        queryClient.invalidateQueries({ queryKey: ['curation', 'similar'], refetchType: 'none' });
      }

      // A member that failed kept the category the strip showed, and that category is
      // exactly what a retry sends back as expectedCategory — so a 409 would repeat for
      // ever. Refetch THIS anchor's cohort for real (last, so it outranks the 'none'
      // invalidation above). The controls re-lock while it is in flight, and `deselected`
      // is keyed by pageId and only reset when the anchor changes, so the curator's
      // selection survives. A member that left the cohort meanwhile simply disappears.
      if (resolution.failedMemberIds.length > 0) {
        queryClient.invalidateQueries({
          queryKey: ['curation', 'similar', input.item.pageId],
          refetchType: 'active',
        });
      }
    },
  });

  const skip = useCallback(() => {
    if (current) setHandled((prev) => new Set(prev).add(current.pageId));
  }, [current]);

  const fetchMore = useCallback(() => {
    setHandled(new Set());
    queryClient.invalidateQueries({ queryKey: ['curation', 'queue'] });
  }, [queryClient]);

  return {
    loading: queueViewIsLoading({
      isLoading: queue.isLoading,
      awaitingRefresh: awaitingQueueRefresh,
      hasCurrent: !!current,
    }),
    error: queue.isError,
    refetch: queue.refetch,
    total: queue.data?.total ?? 0,
    loaded: queue.data?.items.length ?? 0,
    pending,
    current,
    stats,
    members,
    selectedMembers,
    selectedMemberIds,
    toggleMember,
    /** False while the anchor's cohort is still loading — the verdict controls stay locked. */
    membersSettled,
    /** Pages a CORRECTION lands on: the anchor plus every selected member. */
    blastRadius: 1 + selectedMembers.length,
    /**
     * Pages a CONFIRM lands on. Smaller than `blastRadius` when the anchor is
     * uncategorized: confirming "no category" must not strip labelled members.
     */
    confirmRadius: current ? participatingCount(current, 'confirmed', undefined, selectedMembers) : 1,
    confirm: (item: CurationItem) => verdictMutation.mutate({ item, verdict: 'confirmed', members: selectedMembers }),
    /**
     * `members` defaults to the live selection. The category picker passes its own,
     * narrower list instead — the group as it stood when the dialog opened (DR-3318).
     */
    correct: (item: CurationItem, newCategory: string | null, members = selectedMembers) =>
      verdictMutation.mutate({ item, verdict: 'corrected', newCategory, members }),
    skip,
    fetchMore,
    saving: verdictMutation.isPending,
  };
}
